import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import ApplicantContractApi from "../../../../../infrastructure/api/applicantContract";
import ApplicantContractFeeSettingsApi from "../../../../../infrastructure/api/applicantContractFeeSettings";
import ContractorRegistrationApi from "../../../../../infrastructure/api/contractorRegistration";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";
import { toJalaliFullDate } from "../../../../middleware/middleware";
import RegisterContractPaymentDialog from "./RegisterContractPaymentDialog";

const FEE_SETTINGS_PATH = "/SLN/SolarentOperator/ApplicantContractFeeSettings";
const pricingWorkspacePath = (requestId, { recalc = false } = {}) => {
  const q = new URLSearchParams({ section: "quotes-contract" });
  if (recalc) q.set("recalc", "1");
  return `/SLN/SolarentOperator/ProjectWorkspace/${requestId}?${q.toString()}`;
};
const toRialFieldValue = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? String(Math.round(n)) : "";
};

const PAGE_TITLE = "قراردادهای متقاضیان";
const PAGE_SIZE = 50;

const LIST_TABS = [
  { key: "ready", label: "آماده ایجاد" },
  { key: "awaitingApplicantSign", label: "ایجاد شده — امضا نشده" },
  { key: "signed", label: "امضا شده" },
  { key: "signedAwaitingPayment", label: "امضا شده — بدون تراکنش پرداخت" },
  { key: "cancelled", label: "لغو شده" },
  { key: "projectCompleted", label: "پروژه اتمام‌شده" },
];

const STATUS_BADGE_COLORS = {
  "تولید نشده": "default",
  "پیش‌نویس": "default",
  "تولید شده": "info",
  "در انتظار امضا": "warning",
  "تأیید متقاضی": "primary",
  "تایید متقاضی": "primary",
  "تأیید سولارنت": "secondary",
  "تایید سولارنت": "secondary",
  "نهایی شده": "success",
  "نهایی‌شده": "success",
  "امضا شده": "success",
  "لغو شده": "error",
};

const normalizeRow = (raw) => ({
  requestId: raw?.requestId ?? raw?.RequestId,
  applicantContractId: raw?.applicantContractId ?? raw?.ApplicantContractId,
  contractNumber: raw?.contractNumber ?? raw?.ContractNumber ?? "",
  contractVersion: raw?.contractVersion ?? raw?.ContractVersion,
  applicantName: raw?.applicantName ?? raw?.ApplicantName ?? "",
  applicantMobile: raw?.applicantMobile ?? raw?.ApplicantMobile ?? "",
  applicantNationalIdOrLegalId:
    raw?.applicantNationalIdOrLegalId ?? raw?.ApplicantNationalIdOrLegalId ?? "",
  projectCode: raw?.projectCode ?? raw?.ProjectCode ?? "",
  provinceTitle: raw?.provinceTitle ?? raw?.ProvinceTitle ?? "",
  cityTitle: raw?.cityTitle ?? raw?.CityTitle ?? "",
  capacityKw: raw?.capacityKw ?? raw?.CapacityKw ?? "",
  systemTypeTitle: raw?.systemTypeTitle ?? raw?.SystemTypeTitle ?? "",
  requestStatusTitle: raw?.requestStatusTitle ?? raw?.RequestStatusTitle ?? "",
  contractStatus: raw?.contractStatus ?? raw?.ContractStatus,
  contractStatusTitle: raw?.contractStatusTitle ?? raw?.ContractStatusTitle ?? "تولید نشده",
  pdfSha256: raw?.pdfSha256 ?? raw?.PdfSha256 ?? "",
  createdDate: raw?.createdDate ?? raw?.CreatedDate,
  finalizedAt: raw?.finalizedAt ?? raw?.FinalizedAt,
  signedAt: raw?.signedAt ?? raw?.SignedAt ?? null,
  canGenerate: raw?.canGenerate ?? raw?.CanGenerate ?? false,
  canDownloadPdf: raw?.canDownloadPdf ?? raw?.CanDownloadPdf ?? false,
  canDownloadDocx: raw?.canDownloadDocx ?? raw?.CanDownloadDocx ?? false,
  latestValidationErrorSummary:
    raw?.latestValidationErrorSummary ?? raw?.LatestValidationErrorSummary ?? "",
  totalCostRial: (() => {
    const n = Number(raw?.totalCostRial ?? raw?.TotalCostRial);
    return Number.isFinite(n) && n > 0 ? n : null;
  })(),
  blockingReasons:
    raw?.blockingReasons ??
    raw?.BlockingReasons ??
    (raw?.latestValidationErrorSummary || raw?.LatestValidationErrorSummary
      ? String(raw?.latestValidationErrorSummary ?? raw?.LatestValidationErrorSummary)
          .split(/\r?\n/)
          .map((x) => x.trim())
          .filter(Boolean)
      : []),
});

const getGenerateLabel = (row) => {
  if (!row.applicantContractId) return "تولید قرارداد";
  if (row.contractStatus === 5) return null;
  if (row.contractStatus === 9) return "تولید مجدد قرارداد";
  return "تولید نسخه جدید";
};

const saveBlob = (blob, fileName, mimeType) => {
  const url = window.URL.createObjectURL(new Blob([blob], { type: mimeType }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(url);
};

const ApplicantContractsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [listTab, setListTab] = useState(0);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [provinceId, setProvinceId] = useState("");
  const [cityId, setCityId] = useState("");
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [busyRequestId, setBusyRequestId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const [includeGridConnectionAndMeter, setIncludeGridConnectionAndMeter] =
    useState(false);
  const [meterDayPriceRial, setMeterDayPriceRial] = useState("");
  const [installationWageRial, setInstallationWageRial] = useState("");
  const [platformFeeRial, setPlatformFeeRial] = useState("");
  const [errorRow, setErrorRow] = useState(null);
  const [paymentRow, setPaymentRow] = useState(null);
  const loadSeqRef = useRef(0);

  const activeListTabKey = LIST_TABS[listTab]?.key || "ready";

  const loadLocationOptions = useCallback(async () => {
    try {
      const [provincesRes, citiesRes] = await Promise.all([
        ContractorRegistrationApi.getProvinces(),
        ContractorRegistrationApi.getCities(),
      ]);
      const provinceRows = provincesRes?.data ?? provincesRes ?? [];
      const cityRows = citiesRes?.data ?? citiesRes ?? [];
      setProvinces(Array.isArray(provinceRows) ? provinceRows : []);
      setCities(Array.isArray(cityRows) ? cityRows : []);
    } catch {
      setProvinces([]);
      setCities([]);
    }
  }, []);

  const buildParams = useCallback(() => {
    const params = {
      page: page + 1,
      pageSize: rowsPerPage,
      sortBy: "createdDate",
      sortDirection: 2,
      listTab: activeListTabKey,
    };
    if (appliedSearch.trim()) params.search = appliedSearch.trim();
    if (provinceId) params.provinceId = Number(provinceId);
    if (cityId) params.cityId = Number(cityId);
    return params;
  }, [appliedSearch, provinceId, cityId, page, rowsPerPage, activeListTabKey]);

  const loadList = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setRows([]);
    setTotalCount(0);
    try {
      const res = await ApplicantContractApi.listApplicantContracts(buildParams());
      if (seq !== loadSeqRef.current) return;
      if (!res.ok) {
        toast.error(res.message || "بارگذاری لیست قراردادها ناموفق بود.");
        setRows([]);
        setTotalCount(0);
        return;
      }
      const items = res.data?.items ?? res.data?.Items ?? [];
      setRows(items.map(normalizeRow));
      setTotalCount(Number(res.data?.totalCount ?? res.data?.TotalCount) || 0);
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      toast.error(e?.message || "خطا در بارگذاری لیست");
      setRows([]);
      setTotalCount(0);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadLocationOptions();
  }, [loadLocationOptions]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const filteredCities = useMemo(
    () =>
      cities.filter(
        (c) =>
          !provinceId ||
          Number(c.provinceId ?? c.ProvinceId) === Number(provinceId)
      ),
    [cities, provinceId]
  );

  const tableRows = useMemo(
    () =>
      rows.map((row, index) => ({
        ...row,
        row_index: page * rowsPerPage + index + 1,
        capacityDisplay: row.capacityKw ? `${row.capacityKw} kW` : "—",
        contractStatusBadge: row.contractStatusTitle,
        contractStatusColor:
          STATUS_BADGE_COLORS[row.contractStatusTitle] || "default",
        signedAtDisplay: row.signedAt ? toJalaliFullDate(row.signedAt) || "—" : "—",
      })),
    [rows, page, rowsPerPage]
  );

  const isAwaitingPaymentTab = activeListTabKey === "signedAwaitingPayment";
  const isSignedTab = activeListTabKey === "signed" || isAwaitingPaymentTab;

  const headCells = useMemo(
    () => [
      { id: "applicantName", label: "نام متقاضی" },
      ...(isSignedTab
        ? [{ id: "signedAtDisplay", label: "تاریخ امضا" }]
        : []),
      { id: "applicantMobile", label: "موبایل" },
      { id: "applicantNationalIdOrLegalId", label: "کد ملی / شناسه ملی" },
      { id: "projectCode", label: "کد پروژه" },
      { id: "cityTitle", label: "شهر" },
      { id: "capacityDisplay", label: "ظرفیت" },
      {
        id: "totalCostRial",
        label: "هزینه همه",
        render: (value) =>
          value != null && Number(value) > 0
            ? `${Number(value).toLocaleString("fa-IR")} ریال`
            : "—",
      },
      { id: "requestStatusTitle", label: "وضعیت درخواست" },
      {
        id: "contractStatusBadge",
        label: "وضعیت قرارداد",
        type: "chip",
        model: { colorField: "contractStatusColor" },
      },
      { id: "contractNumber", label: "شماره قرارداد" },
      { id: "contractVersion", label: "نسخه" },
    ],
    [isSignedTab]
  );

  const handleApplyFilters = () => {
    setPage(0);
    setAppliedSearch(search);
  };

  const openGenerateConfirm = useCallback(async (row) => {
    if (!row?.canGenerate || busyRequestId != null) return;
    setIncludeGridConnectionAndMeter(false);
    setMeterDayPriceRial("");
    setInstallationWageRial("");
    setPlatformFeeRial("");
    setConfirmRow(row);
    try {
      const res = await ApplicantContractFeeSettingsApi.getCurrent();
      if (!res.ok) return;
      const d = res.data || {};
      setMeterDayPriceRial(
        toRialFieldValue(d.meterDayPriceRial ?? d.MeterDayPriceRial)
      );
      setInstallationWageRial(
        toRialFieldValue(d.installationWageRial ?? d.InstallationWageRial)
      );
      setPlatformFeeRial(
        toRialFieldValue(d.platformFeeRial ?? d.PlatformFeeRial)
      );
    } catch {
      // keep empty; operator can type manually
    }
  }, [busyRequestId]);

  const parseRialInput = (value) => {
    const n = Number(String(value ?? "").replace(/,/g, "").trim());
    return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
  };

  const gridPackageTotal =
    parseRialInput(meterDayPriceRial) +
    parseRialInput(installationWageRial) +
    parseRialInput(platformFeeRial);

  const formatRialDisplay = (value) =>
    Number(value || 0).toLocaleString("fa-IR");

  const isGenerating = busyRequestId != null;

  const handleGenerateConfirmed = async () => {
    const row = confirmRow;
    if (!row?.requestId || isGenerating) return;

    if (includeGridConnectionAndMeter && gridPackageTotal <= 0) {
      toast.error(
        "با تیک اتصال به شبکه، حداقل یکی از مبالغ قیمت روز کنتور، دستمزد نصب یا کارمزد پلتفرم را وارد کنید."
      );
      return;
    }

    setBusyRequestId(row.requestId);
    try {
      const res = await ApplicantContractApi.generateApplicantContract(
        row.requestId,
        {
          includeGridConnectionAndMeter,
          refreshEquipmentSnapshotFromLatestPackage: true,
          // بدون تیک، مبالغ پیش‌فرض تنظیمات نباید به API برود (بک‌اند قبلاً با مبلغ>۰ تیک را اجباری می‌کرد).
          meterDayPriceRial: includeGridConnectionAndMeter
            ? parseRialInput(meterDayPriceRial)
            : 0,
          installationWageRial: includeGridConnectionAndMeter
            ? parseRialInput(installationWageRial)
            : 0,
          platformFeeRial: includeGridConnectionAndMeter
            ? parseRialInput(platformFeeRial)
            : 0,
          gridConnectionAndMeterFeeRial: includeGridConnectionAndMeter
            ? gridPackageTotal
            : 0,
        }
      );
      if (res.ok) {
        toast.success(res.message || "قرارداد متقاضی تولید شد.");
        setConfirmRow(null);
        await loadList();
      } else {
        toast.error(res.message || "خطا در تولید قرارداد متقاضی.");
      }
    } catch (e) {
      toast.error(e?.message || "خطا در تولید قرارداد");
    } finally {
      setBusyRequestId(null);
    }
  };

  const handleDownloadPdf = async (row) => {
    if (!row.applicantContractId) return;
    setDownloadingId(`pdf-${row.applicantContractId}`);
    try {
      const blob = await ApplicantContractApi.downloadApplicantContractPdf(
        row.applicantContractId
      );
      saveBlob(
        blob,
        `applicant-contract-${row.contractNumber || row.applicantContractId}.pdf`,
        "application/pdf"
      );
    } catch {
      toast.error("خطا در دانلود PDF.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadDocx = async (row) => {
    if (!row.applicantContractId) return;
    setDownloadingId(`docx-${row.applicantContractId}`);
    try {
      const blob = await ApplicantContractApi.downloadApplicantContractDocx(
        row.applicantContractId
      );
      saveBlob(
        blob,
        `applicant-contract-${row.contractNumber || row.applicantContractId}.docx`,
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      );
    } catch {
      toast.error("خطا در دانلود DOCX.");
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePreviewHtml = async (row) => {
    if (!row?.requestId) return;
    setDownloadingId(`preview-${row.requestId}`);
    try {
      const res = await ApplicantContractApi.previewApplicantContractHtml(row.requestId);
      if (!res.ok || !res.data) {
        toast.error("پیش‌نمایش HTML در دسترس نیست. ابتدا قرارداد را تولید کنید.");
        return;
      }
      const blob = new Blob([res.data], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("خطا در پیش‌نمایش HTML.");
    } finally {
      setDownloadingId(null);
    }
  };

  const goToRecalculate = (row) => {
    if (!row?.requestId) return;
    navigate(pricingWorkspacePath(row.requestId, { recalc: true }));
  };

  const tableActions = useMemo(
    () => [
      {
        icon: <ReceiptLongOutlinedIcon fontSize="small" />,
        onClick: (row) => setPaymentRow(row),
        color: "success",
        tooltip: "ثبت تراکنش پرداخت و آپلود رسید",
        hidden: (row) => !isAwaitingPaymentTab || !row.requestId,
      },
      {
        icon: <ArticleOutlinedIcon fontSize="small" />,
        onClick: openGenerateConfirm,
        color: "primary",
        tooltip: "تولید قرارداد متقاضی",
        hidden: (row) => !row.canGenerate || busyRequestId === row.requestId,
      },
      {
        icon: <PercentOutlinedIcon fontSize="small" />,
        onClick: goToRecalculate,
        color: "success",
        tooltip: "محاسبه مجدد کارمزد و تخفیف",
        hidden: (row) => !row.requestId,
      },
      {
        icon: <VisibilityOutlinedIcon fontSize="small" />,
        onClick: handlePreviewHtml,
        color: "default",
        tooltip: "پیش‌نمایش HTML (از Snapshot)",
        hidden: (row) =>
          !row.applicantContractId ||
          downloadingId === `preview-${row.requestId}`,
      },
      {
        icon: <DownloadIcon fontSize="small" />,
        onClick: handleDownloadPdf,
        color: "info",
        tooltip: "دانلود PDF",
        hidden: (row) =>
          !row.canDownloadPdf ||
          downloadingId === `pdf-${row.applicantContractId}`,
      },
      {
        icon: <DescriptionOutlinedIcon fontSize="small" />,
        onClick: handleDownloadDocx,
        color: "secondary",
        tooltip: "دانلود DOCX",
        hidden: (row) =>
          !row.canDownloadDocx ||
          downloadingId === `docx-${row.applicantContractId}`,
      },
      {
        icon: <ErrorOutlineIcon fontSize="small" />,
        onClick: (row) => setErrorRow(row),
        color: "warning",
        tooltip: "مشاهده خطا / جزئیات اعتبارسنجی",
        hidden: (row) => !row.latestValidationErrorSummary,
      },
    ],
    [busyRequestId, downloadingId, navigate, openGenerateConfirm, isAwaitingPaymentTab]
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={PAGE_TITLE}
            subheader="فقط متقاضیانی که به مرحله قرارداد رسیده‌اند (پذیرش پیشنهاد قیمت یا دارای قرارداد) — خروجی رسمی: PDF"
            action={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Button
                  component={RouterLink}
                  to={FEE_SETTINGS_PATH}
                  size="small"
                  startIcon={<SettingsOutlinedIcon />}
                >
                  تنظیمات هزینه
                </Button>
                <IconButton color="primary" onClick={loadList} aria-label="بروزرسانی">
                  <SyncIcon />
                </IconButton>
              </Stack>
            }
          />
          <CardContent>
            <Tabs
              value={listTab}
              onChange={(_, next) => {
                setListTab(next);
                setPage(0);
              }}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{ mb: 1.5, borderBottom: 1, borderColor: "divider" }}
            >
              {LIST_TABS.map((tab) => (
                <Tab key={tab.key} label={tab.label} />
              ))}
            </Tabs>

            <Box
              sx={{
                mb: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleApplyFilters()}
                placeholder="جستجو بر اساس نام، موبایل، کد ملی، کد پروژه..."
                sx={{ flex: "1 1 260px", minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>استان</InputLabel>
                <Select
                  label="استان"
                  value={provinceId}
                  onChange={(e) => {
                    setProvinceId(e.target.value);
                    setCityId("");
                    setPage(0);
                  }}
                >
                  <MenuItem value="">همه</MenuItem>
                  {provinces.map((p) => (
                    <MenuItem key={p.id ?? p.Id} value={p.id ?? p.Id}>
                      {p.provinceName ?? p.ProvinceName ?? p.title ?? p.Title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 140 }} disabled={!provinceId}>
                <InputLabel>شهر</InputLabel>
                <Select
                  label="شهر"
                  value={cityId}
                  onChange={(e) => {
                    setCityId(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">همه</MenuItem>
                  {filteredCities.map((c) => (
                    <MenuItem key={c.id ?? c.Id} value={c.id ?? c.Id}>
                      {c.cityName ?? c.CityName ?? c.title ?? c.Title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button variant="contained" onClick={handleApplyFilters}>
                اعمال فیلتر
              </Button>

              <Chip size="small" variant="outlined" label={`${totalCount} مورد`} />
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {LIST_TABS[listTab]?.label || "لیست قراردادها"}
            </Typography>

            <EnhancedTable
              rowKey="requestId"
              rows={tableRows}
              headCells={headCells}
              actions={tableActions}
              actionsMode="menu"
              loading={loading}
              pageSize={rowsPerPage}
              serverPagination={{
                totalCount,
                page,
                rowsPerPage,
                onPageChange: (newPage) => setPage(newPage),
                onRowsPerPageChange: (next) => {
                  setRowsPerPage(next);
                  setPage(0);
                },
              }}
            />
          </CardContent>
        </Card>
      </Grid>

      <Dialog
        open={Boolean(confirmRow)}
        onClose={() => {
          if (!isGenerating) setConfirmRow(null);
        }}
        disableEscapeKeyDown={isGenerating}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>تولید قرارداد متقاضی</DialogTitle>
        <DialogContent>
          <DialogContentText>
            آیا از تولید قرارداد متقاضی برای این درخواست مطمئن هستید؟
          </DialogContentText>
          {confirmRow ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                متقاضی: {confirmRow.applicantName || "—"}
              </Typography>
              <Typography variant="body2">
                کد پروژه: {confirmRow.projectCode || "—"}
              </Typography>
              {confirmRow.applicantContractId ? (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                  {getGenerateLabel(confirmRow)}
                </Typography>
              ) : null}

              <Box
                sx={{
                  mt: 2.5,
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeGridConnectionAndMeter}
                      disabled={isGenerating}
                      onChange={(e) =>
                        setIncludeGridConnectionAndMeter(e.target.checked)
                      }
                    />
                  }
                  label="اتصال به شبکه و نصب کنتور جزء تعهدات اجرایی قرارداد باشد"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked
                      disabled
                    />
                  }
                  label="اسنپ‌شات تجهیزات و قیمت تمام‌شده همیشه از آخرین نسخه پکیج خوانده می‌شود"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: includeGridConnectionAndMeter ? 1.5 : 0 }}
                >
                  با هر بار تولید، تجهیزات و مبلغ تمام‌شده از پکیج فعلی بازسازی می‌شود
                  (نسخهٔ قبلی اسنپ‌شات برای استناد باقی می‌ماند). تیک کنتور نیز مبالغ را از
                  تنظیمات پیش‌فرض پر می‌کند و به مبلغ نهایی قرارداد اضافه
                  می‌کند.
                </Typography>

                {includeGridConnectionAndMeter ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                      gap: 1.25,
                    }}
                  >
                    <TextField
                      size="small"
                      type="number"
                      label="قیمت روز کنتور (ریال)"
                      value={meterDayPriceRial}
                      disabled={isGenerating}
                      onChange={(e) => setMeterDayPriceRial(e.target.value)}
                      inputProps={{ min: 0, step: 1000 }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="دستمزد نصب (ریال)"
                      value={installationWageRial}
                      disabled={isGenerating}
                      onChange={(e) => setInstallationWageRial(e.target.value)}
                      inputProps={{ min: 0, step: 1000 }}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="کارمزد پلتفرم (ریال)"
                      value={platformFeeRial}
                      disabled={isGenerating}
                      onChange={(e) => setPlatformFeeRial(e.target.value)}
                      inputProps={{ min: 0, step: 1000 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ gridColumn: "1 / -1", fontWeight: 600 }}
                    >
                      جمع افزودنی به قرارداد: {formatRialDisplay(gridPackageTotal)}{" "}
                      ریال
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRow(null)} disabled={isGenerating}>
            انصراف
          </Button>
          <Button
            color="success"
            variant="outlined"
            disabled={isGenerating || !confirmRow?.requestId}
            startIcon={<PercentOutlinedIcon />}
            onClick={() => {
              const row = confirmRow;
              setConfirmRow(null);
              goToRecalculate(row);
            }}
          >
            محاسبه مجدد
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateConfirmed}
            disabled={isGenerating}
            autoFocus
            startIcon={
              isGenerating ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isGenerating ? "در حال تولید..." : "تأیید"}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={isGenerating}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          color: "#fff",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">در حال تولید قرارداد...</Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          ساخت PDF ممکن است کمی طول بکشد؛ لطفاً صفحه را نبندید.
        </Typography>
      </Backdrop>

      <Dialog
        open={Boolean(errorRow)}
        onClose={() => setErrorRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>جزئیات اعتبارسنجی</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            component="div"
            sx={{ whiteSpace: "pre-line" }}
          >
            {errorRow?.latestValidationErrorSummary || "خطایی ثبت نشده است."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorRow(null)}>بستن</Button>
        </DialogActions>
      </Dialog>

      <RegisterContractPaymentDialog
        row={paymentRow}
        onClose={() => setPaymentRow(null)}
        onRegistered={() => {
          setPaymentRow(null);
          loadList();
        }}
      />
    </Grid>
  );
};

export default ApplicantContractsPage;
